import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  UntypedFormGroup,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  State,
  process,
  SortDescriptor,
  CompositeFilterDescriptor,
  filterBy,
} from '@progress/kendo-data-query';
import {
  GridDataResult,
  GridComponent,
  FilterService,
  GridModule,
} from '@progress/kendo-angular-grid';
import { Observable, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UserManagementService } from './user-management.service';
import { AddUserComponent } from './add-user/add-user.component';
import { EditUserComponent } from './edit-user/edit-user.component';
import { RolesRightsService } from '../roles-rights/roles-rights.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomFiltersService } from '../../services/custom-filters.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { UserData } from '../../models/admin-models';
import { NgxLoadingModule } from 'ngx-loading';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MultiSelectModule } from '@progress/kendo-angular-dropdowns';
import { FilterValuesPipe } from '../../filter-value.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  imports: [
    NgxLoadingModule,
    UtilityToolbarComponent,
    MatSelectModule,
    GridModule,
    MatIconModule,
    MultiSelectModule,
    FilterValuesPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
  ],
})
export class UserManagementComponent implements OnInit {
  private userService = inject(UserManagementService);
  private rbs = inject(RightBridgeApiService);
  private rolesService = inject(RolesRightsService);
  dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private cstmFltrSrvc = inject(CustomFiltersService);
  private ss = inject(SessionStorageService);

  visible = true;
  selectable = true;
  removable = true;
  openIndex: number;
  noRecordMessage = 'Perform a search to find users';
  separatorKeysCodes: number[] = [ENTER, COMMA];
  searchForm: FormGroup;
  lastPage = false;
  public pageSize = 15;
  public pageableData = {
    buttonCount: 5,
    refresh: true,
    alwaysVisible: false,
    pageSizes: [this.pageSize, 25, 50, 100],
  };
  public gridView: GridDataResult;
  public gridForm: FormGroup = new UntypedFormGroup({});
  public multiple = false;
  public allowUnsort = true;
  rights: Array<string> = [];
  roleData = [];
  saveTypeData = [];
  filteredRoles: Observable<string[]>;
  roles = [];
  allRoles = ['Administrator', 'AW Rep'];
  allSaveTypes = [
    { name: 'None', id: 0 },
    { name: 'Permanent', id: 1 },
    { name: 'Override', id: 2 },
  ];
  reps = [];
  allUsers = [];
  proxyData = [];
  filterData = {};
  gridColumns = [
    {
      DataField: 'id',
      Filter: true,
    },
    {
      DataField: 'firstName',
      Filter: true,
    },
    {
      DataField: 'lastName',
      Filter: true,
    },
    {
      DataField: 'roleSum',
      Filter: true,
    },
    {
      DataField: 'unit',
      Filter: true,
    },
    {
      DataField: 'saveTypeTerm',
      Filter: true,
    },
  ];
  filter: CompositeFilterDescriptor = {} as CompositeFilterDescriptor;
  sort = null;
  proxySelection: UserData[];
  filteredUsers: Observable<any>;
  loading = false;
  gridData: unknown[];

  @ViewChild('grid', { static: false }) grid: GridComponent;

  public state: State = {
    skip: 0,
    take: this.pageSize,
    sort: this.sort,
    filter: {
      logic: 'and',
      filters: [{ field: 'name', operator: 'contains', value: '' }],
    },
  };
  public userData: { users: UserData[] } = { users: [] };

  constructor() {
    this.filter = { filters: [], logic: 'or' };
    this.rolesService.getRoles().subscribe(roles => {
      this.allRoles = roles['roles'].map(r => r['name']);
      this.roleData = roles['roles'].map(r => {
        return { id: r['id'], name: r['name'] };
      });
    });

    const formGroup = {};
    formGroup['id'] = new FormControl();
    formGroup['firstName'] = new FormControl();
    formGroup['lastName'] = new FormControl();
    formGroup['username'] = new FormControl();
    formGroup['unit'] = new FormControl();
    formGroup['repcode'] = new FormControl();
    formGroup['role'] = new FormControl();
    formGroup['saveType'] = new FormControl();
    this.searchForm = new UntypedFormGroup(formGroup);
  }

  ngOnInit() {
    this.rights = this.ss.get('rights') || [];
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: 2000,
    });
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.state['sort'] = this.sort;
    this.gridView = process(this.userData['users'], this.state);
  }

  openCreateUserDialog(index = null) {
    const dialogRef = this.dialog.open(AddUserComponent, {
      panelClass: 'role-dialog',
      data: { role: this.roles[index - 1], headline: 'Add Role' },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addUser(result['data']);
      }
    });
  }

  userLookAhead(event) {
    this.userService
      .advisorLookahead(event.target.value)
      .subscribe((users: { users: UserData[] }) => {
        this.proxySelection = users.users;
        this.filteredUsers = of(users.users.map(u => u.name));
      });
  }

  newSearch() {
    this.loading = true;
    this.resetSearch();
    this.searchUsers();
  }

  resetSearch() {
    this.userData = { users: [] };
    this.state['skip'] = 0;
    this.lastPage = false;
  }

  clearSearch() {
    this.searchForm.reset();
  }

  searchUsers() {
    let start = 0;
    if (this.userData['users']) {
      start = this.userData['users'].length;
    }
    this.userService.searchUsers(this.searchForm.value, start).subscribe({
      next: users => {
        this.noRecordMessage = 'No Users Found';
        if (users['users'].length === 0) {
          this.lastPage = true;
        }
        if (this.userData['users']) {
          this.userData['users'] = this.userData['users'].concat(
            users['users']
          );
        } else {
          this.userData = users;
        }
        this.processUsers(this.userData['users']);
        this.buildFilterOptions();
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.openSnackBar('Error searching. Please try again.');
        console.error('There was an error searching: ', err);
      },
    });
  }

  addUser(data) {
    this.userService.add(data).subscribe(() => {
      this.openSnackBar('User Created.  Loading Result...');
      this.rbs.getRepCodes(' ').subscribe(() => {
        this.userService
          .searchUsers({ id: '=' + data['userid'] })
          .subscribe((users: { users: UserData[] }) => {
            if (users['users'] instanceof Array) {
              this.userData = users;
              this.processUsers(this.userData['users']);
              this.gridView = process(this.userData['users'], this.state);
            }
          });
      });
    });
  }

  addProxyData(name) {
    const data = this.proxySelection.find(u => u['name'] === name);
    this.proxyData.push(data);
  }

  processUsers(users: Array<any>) {
    users.forEach(u => {
      u['repSum'] = this.getFieldSummary(u, 'repCodes');
      u['roleSum'] = this.getFieldSummary(u, 'roles');
      u['unitSum'] = this.getFieldSummary(u, 'unitsManaged');
      switch (u['saveType']) {
        case '0':
          u['saveTypeTerm'] = 'None';
          break;
        case '1':
          u['saveTypeTerm'] = 'Permanent';
          break;
        case '2':
          u['saveTypeTerm'] = 'Override';
          break;
      }
      u['component'] = this;
    });
  }

  getFieldSummary(data: any, field: string): string {
    if (data[field] && data[field].length > 0) {
      return data[field][0];
    } else {
      return '';
    }
  }

  editUser(data) {
    const dialogRef = this.dialog.open(EditUserComponent, {
      panelClass: 'role-dialog',
      data: {
        user: data,
        roleData: this.roleData,
        allRoles: this.allRoles,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.removedUser) {
        for (let i = 0; i < this.userData['users'].length; i++) {
          if (this.userData['users'][i].id === result.removedUser['id']) {
            this.userData['users'].splice(i, 1);
            this.gridView = process(this.userData['users'], this.state);
          }
        }
      }
      if (result?.response) {
        this.newSearch();
      }
    });
  }

  buildFilterOptions() {
    this.filterData = this.cstmFltrSrvc.buildColumnFilterOptions(
      this.userData['users'],
      this.gridColumns
    );
  }

  public filterValueChange(
    values: any[],
    field,
    filterService: FilterService
  ): void {
    filterService.filter({
      filters: values.map(value => ({
        field: field,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    this.gridData = filterBy(this.userData['users'], filter);
  }
}
